import React, { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle, AlertCircle, ExternalLink, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../LanguageContext';
import { createUpayPayment, getUpayStatus } from '../../services/paymentService';
import { USD_TO_NIS } from '../data/characters';

/**
 * UpayPayment — UPay Israeli credit-card gateway (upay.co.il).
 *
 * Flow:
 *   1. POST /api/payments/upay/create → server creates pending payment_intent
 *      and returns the exact form fields to submit (action + fields).
 *   2. We open a centered popup, build an HTML form targeting that popup,
 *      and submit it → user goes to UPay's secure page.
 *   3. UPay calls our IPN webhook with the payment result (server-side).
 *   4. Meanwhile we poll /api/payments/upay/status/:uuid every 2s.
 *   5. On 'succeeded' → close popup, fire onSuccess.
 *
 * Popup is preferred (matches PayPlus UX); falls back to same-tab redirect
 * if the browser blocks popups.
 */
export default function UpayPayment({ plan, onSuccess, onError, onCancel }) {
  const { language } = useLanguage();
  const isHe = language === 'he';

  const [phase, setPhase] = useState('idle');   // idle | creating | waiting | success | error
  const [uuid, setUuid] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const closeCheckRef = useRef(null);

  const amount  = plan?.totalPrice ?? plan?.price ?? 0;
  const credits = plan?.totalCredits ?? plan?.credits ?? 0;
  const currency = 'ILS'; // UPay supports NIS only via this flow

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (closeCheckRef.current) clearInterval(closeCheckRef.current);
  }, []);

  // Build + submit a hidden HTML form into the (already opened) popup
  const submitForm = (action, fields, target) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;
    form.target = target;
    form.style.display = 'none';

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value == null ? '' : String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    setTimeout(() => form.remove(), 1000);
  };

  const pollStatus = async (id) => {
    try {
      const status = await getUpayStatus(id);
      if (status.status === 'succeeded') {
        clearInterval(pollRef.current);
        if (closeCheckRef.current) clearInterval(closeCheckRef.current);
        if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
        setPhase('success');
        if (onSuccess) onSuccess({
          provider: 'upay',
          credits: status.credits,
          amount: status.amount,
          currency: status.currency,
          uuid: id,
        });
      } else if (status.status === 'failed') {
        clearInterval(pollRef.current);
        if (closeCheckRef.current) clearInterval(closeCheckRef.current);
        if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
        setPhase('error');
        setErrorMsg(isHe ? 'התשלום נכשל. נסה שוב.' : 'Payment failed. Please try again.');
      }
      // status === 'pending' → keep polling
    } catch (err) {
      // 404 right after creation is possible — keep polling, don't crash
      console.warn('[upay] status poll error:', err.message);
    }
  };

  const handleStart = async () => {
    try {
      setPhase('creating');
      setErrorMsg('');

      const data = await createUpayPayment({ amount, credits });
      setUuid(data.uuid);

      // Open empty popup FIRST (must be inside the click handler to avoid
      // popup-blockers). Then submit the form into it.
      const w = 700, h = 850;
      const left = (window.screen.width / 2) - (w / 2);
      const top  = (window.screen.height / 2) - (h / 2);
      const popupName = `upay_payment_${data.uuid}`;
      popupRef.current = window.open(
        'about:blank',
        popupName,
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!popupRef.current || popupRef.current.closed) {
        // Popup blocked → fall back to same-tab redirect
        submitForm(data.action, data.fields, '_self');
        return;
      }

      // Submit into the popup
      submitForm(data.action, data.fields, popupName);

      setPhase('waiting');
      pollRef.current = setInterval(() => pollStatus(data.uuid), 2000);

      // Stop polling if user manually closes the popup (give IPN 5s grace)
      closeCheckRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(closeCheckRef.current);
          setTimeout(() => {
            // Final check — if still pending after 5s, give up
            getUpayStatus(data.uuid).then((s) => {
              if (s.status === 'pending') {
                clearInterval(pollRef.current);
                setPhase('idle');
              }
            }).catch(() => {
              clearInterval(pollRef.current);
              setPhase('idle');
            });
          }, 5000);
        }
      }, 1000);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.message || 'UPay error');
      if (onError) onError(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 mb-3">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-white mb-1">
          {isHe ? 'תשלום מאובטח · UPay' : 'Secure Payment · UPay'}
        </h3>
        <p className="text-sm text-slate-400">
          {isHe
            ? 'תועבר/י לדף הסליקה של UPay. כל פרטי האשראי מאובטחים ב-UPay, לא באתר שלנו.'
            : "You'll be redirected to UPay's secure checkout. Card details never touch our site."}
        </p>
      </div>

      {/* Plan summary */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-slate-400">{isHe ? 'קרדיטים' : 'Credits'}</span>
          <span className="text-white font-semibold">{credits}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400">{isHe ? 'סכום לתשלום' : 'Amount'}</span>
          <span className="text-2xl font-bold text-white">
            ₪{Number(amount).toFixed(2)}
            {!isHe && <span className="text-sm text-slate-400 ms-2">(~${(Number(amount) / USD_TO_NIS).toFixed(2)})</span>}
          </span>
        </div>
      </div>

      {(phase === 'idle' || phase === 'creating') && (
        <Button
          onClick={handleStart}
          disabled={phase === 'creating' || !amount || !credits}
          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white h-14 text-base font-semibold gap-2"
        >
          {phase === 'creating' ? (
            <><Loader2 className="w-5 h-5 animate-spin" />{isHe ? 'מכין דף תשלום...' : 'Preparing payment...'}</>
          ) : (
            <><ExternalLink className="w-5 h-5" />{isHe ? 'המשך לתשלום' : 'Proceed to Payment'}</>
          )}
        </Button>
      )}

      {phase === 'waiting' && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-emerald-400 animate-spin shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-semibold mb-1">
                {isHe ? 'ממתין להשלמת התשלום...' : 'Waiting for payment...'}
              </p>
              <p className="text-xs text-slate-400">
                {isHe
                  ? 'השלימ/י את התשלום בחלון שנפתח. החלון יסגר אוטומטית בסיום.'
                  : 'Complete the payment in the popup. It will close automatically when done.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {phase === 'success' && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
          <p className="text-white font-semibold">
            {isHe ? 'התשלום הצליח! מוסיף קרדיטים...' : 'Payment successful! Adding credits...'}
          </p>
        </div>
      )}

      {phase === 'error' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-semibold mb-1">
                {isHe ? 'שגיאה בתשלום' : 'Payment error'}
              </p>
              <p className="text-xs text-red-300 mb-3">{errorMsg}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPhase('idle'); setErrorMsg(''); }}
                className="text-xs"
              >
                {isHe ? 'נסה שוב' : 'Try again'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button
        variant="ghost"
        onClick={onCancel}
        className="w-full text-slate-400 hover:text-white"
      >
        {isHe ? 'ביטול' : 'Cancel'}
      </Button>
    </div>
  );
}
